import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Calendar, MapPin, Clock, Ticket } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCents } from "@/lib/money";
import { formatDate, formatTime } from "@/lib/dates";

/** Seats left, or null when the event has no cap (unlimited). */
export function availability(event) {
  const hasOptions = event.ticket_options && event.ticket_options.length > 0;
  if (hasOptions) {
    const available = event.ticket_options.reduce((sum, o) => sum + Math.max(0, (o.total_tickets || 0) - (o.tickets_sold || 0)), 0);
    return { available, unlimited: false, soldOut: available <= 0 };
  }
  if (event.total_tickets == null) return { available: Infinity, unlimited: true, soldOut: false };
  const available = Math.max(0, event.total_tickets - (event.tickets_sold || 0));
  return { available, unlimited: false, soldOut: available <= 0 };
}

export function eventLocation(event) {
  if (event.venue_name) return [event.venue_name, event.venue_city].filter(Boolean).join(", ");
  return event.location || "";
}

/** The lowest price on offer: the base price, or the cheapest ticket option. */
export function startingPriceCents(event) {
  if (event.ticket_options && event.ticket_options.length > 0) {
    return Math.min(...event.ticket_options.map((o) => o.price_cents || 0));
  }
  return event.price_cents || 0;
}

export default function EventCard({ event }) {
  const { available, unlimited, soldOut } = availability(event);
  const location = eventLocation(event);

  return (
    <Link to={createPageUrl("EventDetail") + `?id=${event.id}`} className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 group">
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-400 to-purple-500" style={{ aspectRatio: "1080/603" }}>
        {event.image_url ? (
          <img
            src={event.image_url}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Ticket className="h-16 w-16 text-white/40" />
          </div>
        )}
        {!unlimited && (
          <div className="absolute top-3 right-3">
            <Badge className={soldOut ? "bg-red-500 text-white border-0" : "bg-green-500 text-white border-0"}>
              {soldOut ? "Sold Out" : `${available} left`}
            </Badge>
          </div>
        )}
        {event.category && (
          <div className="absolute top-3 left-3">
            <Badge variant="secondary" className="bg-white/90 text-gray-700">{event.category}</Badge>
          </div>
        )}
      </div>

      <div className="p-5">
        <h3 className="font-bold text-lg text-gray-900 mb-3 line-clamp-2">{event.title}</h3>

        <div className="space-y-1.5 mb-4">
          {event.start_date && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Calendar className="h-4 w-4 text-indigo-400 flex-shrink-0" />
              {event.end_date && event.end_date !== event.start_date ? (
                <span>{formatDate(event.start_date, "monthDay")} – {formatDate(event.end_date, "short")}</span>
              ) : (
                <span>{formatDate(event.start_date, "long")}</span>
              )}
            </div>
          )}
          {(() => {
            const hasPerDay = !event.same_time_all_days && event.daily_times && event.daily_times.length > 0;
            const firstDay = hasPerDay ? event.daily_times[0] : null;
            const startT = firstDay ? firstDay.start_time : event.start_time;
            const endT = firstDay ? firstDay.end_time : event.end_time;
            if (!startT) return null;
            return (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                <span>{formatTime(startT)}{endT ? ` – ${formatTime(endT)}` : ""}</span>
              </div>
            );
          })()}
          {location && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <MapPin className="h-4 w-4 text-indigo-400 flex-shrink-0" />
              <span className="line-clamp-1">{location}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-gray-50">
          <span className="text-2xl font-bold text-indigo-600">{formatCents(startingPriceCents(event))}</span>
          <Button size="sm" disabled={soldOut} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40">
            {soldOut ? "Sold Out" : "Get Tickets"}
          </Button>
        </div>
      </div>
    </Link>
  );
}
